import { useMemo } from 'react';
import { User, Provider } from 'interfaces';

const sanitize = (value: string | null | undefined) => (value || '').trim();

const buildName = (firstName: string | null, lastName: string | null) => {
  const parts = [sanitize(firstName), sanitize(lastName)].filter(Boolean);
  return parts.join(' ').trim();
};

const selectProviderForUser = (user: User): Provider | null => {
  const providers = user.providers || [];
  if (!providers.length) return null;
  if (user.providerID) {
    const match = providers.find((p) => p.id === user.providerID);
    if (match) return match;
  }
  return providers[0] || null;
};

export const getUserFullName = (user: User | Provider): string => {
  return buildName(user.firstName, user.lastName);
};

export const getUserNameWithTitlePrefix = (user: User | Provider): string => {
  const baseName = buildName(user.firstName, user.lastName);
  if (!baseName) return '';
  if ('providers' in user) {
    const provider = selectProviderForUser(user);
    const title = sanitize(provider?.title);
    return title ? `${title} ${baseName}`.trim() : baseName;
  } else {
    const title = sanitize(user.title);
    return title ? `${title} ${baseName}`.trim() : baseName;
  }
};

export const getUserInitials = (user: User): string => {
  const first = sanitize(user.firstName);
  const last = sanitize(user.lastName);

  const initials: string[] = [];
  if (first) initials.push(first[0]);
  if (last) initials.push(last[0]);

  const result = initials.slice(0, 2).join('').toUpperCase();
  return result;
};

export const useGetUsersDisplayInfo = (user?: User | null) => {
  const fullName = useMemo(() => (user ? getUserFullName(user) : ''), [user]);
  const nameWithTitle = useMemo(
    () => (user ? getUserNameWithTitlePrefix(user) : ''),
    [user]
  );
  const initials = useMemo(() => (user ? getUserInitials(user) : ''), [user]);

  return {
    fullName,
    nameWithTitle,
    initials,
    getUserFullName,
    getUserNameWithTitlePrefix,
    getUserInitials,
  };
};
